// src/portal-app/insights/FinancialPerformanceScreen.js
//
// W2 Task 6 — Financial Performance, the first native Insights surface.
// Phone-first rebuild of src/pages/portal/reporting/FinancialPerformancePage.js
// over the SAME data services, assembled from the Task-5 kit.
//
// Data — reused UNCHANGED from services/api.js:
//   reportingService.getFinancialV2(start, end)   the main payload
//   reportingService.getNarrativeV2(start, end)   enhancement — fails soft
//   reportingService.getTargetsV2()               READ-ONLY (locked decision;
//     saveTargetsV2 does not exist — the web page's edit modal silently
//     fails and is deliberately NOT ported)
//
// Zones: hero revenue + delta → KPI grid → revenue trend → expense breakdown
// → targets progress (read-only) → AI narrative → insights (the endpoint
// section the web page never rendered).
//
// Deliberately dropped from the web page (desktop-only): KPI flip cards, the
// expand-chart modal with period paging, the profit waterfall, the revenue
// forecast (dead code in the portal — its showForecast guard can never be
// true once parentDates is passed), payment/dining pie charts and the period
// comparison table (the KPI captions carry the previous-period values).
//
// Fixes over the web page (per the task brief):
//   • real loading / error+retry / empty states via SurfaceStates — the web
//     page swallows fetch errors and keeps stale data;
//   • targets read the ACTUAL /targets fields (metric_name/target_value) and
//     take the actual from the matching financial KPI — the web card reads
//     name/actual/goal, none of which exist, so it renders "undefined" at 0%;
//     a target whose metric has no matching KPI is OMITTED, never shown at 0.
//
// W4 Task 13 (P6 — spec §5, conservative pass; NO structural rewrite, zone
// order and every chart unchanged):
//   • hero already carried the P4 GlassCard-veil treatment (HeroValue) —
//     confirmed, not re-authored;
//   • the revenue/cost/profit trio the reader had to relate by hand now
//     also renders as a ShareBar (Total Expenses vs Net Profit, both real
//     /financial KPI fields — reporting_v2.py `_build_kpi("Total Expenses",
//     ..., invert=True)` / `_build_kpi("Net Profit", ...)`) inside the hero
//     card. The KPI grid tiles for those two are kept too — they carry the
//     period-over-period delta the ShareBar doesn't, matching the precedent
//     Cost Management already set (COGS/OpEx/Net Margin live as BOTH KPI
//     tiles and a P&L StackedShareBar); dropping them from the grid would be
//     an information loss for no gain. Net Profit can be negative (a loss
//     period) — ShareBar's Task-3 contract already excludes negative
//     segments from the bar draw while keeping them, real value intact, in
//     the legend, so no clamping is needed here;
//   • ShareBar is omitted (not rendered as a bar of two £0.00 legend rows)
//     when both Total Expenses and Net Profit are exactly zero — the
//     collapsed-empty rule;
//   • no qualifying "list" exists on this screen for CondensedRow — the
//     expense breakdown is BarList (a ranking chart, explicitly named a
//     kept chart), the targets are ProgressVsTarget bullets, and insights
//     are InsightList cards; none is the icon/name/value/pill row shape
//     CondensedRow exists for;
//   • PullToRefresh now wraps SurfaceStates (RangeBar stays outside both,
//     unchanged, so it keeps surviving loading/error/empty).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header/back
// button (route meta in nav/routes.js).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { reportingService } from '../../services/api';
import { rangeForPreset, toLocalISO, trendLabel } from '../nav/dateRange';
import { useRangePreset } from './rangeState';
import {
    normaliseKpi,
    fmtMoney,
    fmtMoneyAuto,
    mapAnomaly,
    fillHourlyGaps,
    HeroValue,
    RangeBar,
    KpiGrid,
    InsightList,
    BarList,
    TrendChart,
    ProgressVsTarget,
    NarrativeCard,
    SurfaceStates,
} from './kit';
import { ShareBar, PullToRefresh, ensureKitCss } from '../shell/kit';

/* Screen-local chrome only — everything data-bearing comes from the kit
   (which also injects the shared .insk-* stylesheet, incl. the HeroValue
   hero anatomy shared by all Insights surfaces). */
const CSS = `
.fps-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
.fps-sec{font-size:11px;font-weight:600;letter-spacing:.9px;text-transform:uppercase;
  color:var(--c-textLight);margin:18px 6px 10px;}
`;

/* Backend KPI names are English literals — map them to t() keys.
   total_revenue / net_profit / avg_transaction / total_expenses already
   exist in the DB (shipped portal pages use them); profit_margin /
   breakeven / rev_vs_target are registered by this branch. */
const KPI_LABELS = {
    'Total Revenue': ['total_revenue', 'Total Revenue'],
    'Net Profit': ['net_profit', 'Net Profit'],
    'Profit Margin': ['profit_margin', 'Profit Margin'],
    'Avg Transaction': ['avg_transaction', 'Avg Transaction'],
    'Total Expenses': ['total_expenses', 'Total Expenses'],
    'Breakeven': ['breakeven', 'Breakeven'],
    'Rev vs Target': ['rev_vs_target', 'Rev vs Target'],
};

const MONEY_KPIS = new Set(['Total Revenue', 'Net Profit', 'Avg Transaction', 'Total Expenses']);

/* Read-only targets mapping — /targets rows carry metric_name +
   target_value; the ACTUAL comes from the matching /financial KPI. A metric
   with no matching KPI must be omitted (never rendered at 0%).
   `compact` (M4): the bullet states the actual the same way that figure's
   PRIMARY rendering does — revenue is the hero (always full form), profit
   and avg_transaction are KPI tiles (auto-compact at ≥100k). One screen
   must never state the same number two ways. */
const TARGET_METRICS = {
    revenue: { kpiName: 'Total Revenue', labelKey: 'total_revenue', fallback: 'Total Revenue', money: true, compact: false },
    profit: { kpiName: 'Net Profit', labelKey: 'net_profit', fallback: 'Net Profit', money: true, compact: true },
    margin: { kpiName: 'Profit Margin', labelKey: 'profit_margin', fallback: 'Profit Margin', money: false },
    avg_transaction: { kpiName: 'Avg Transaction', labelKey: 'avg_transaction', fallback: 'Avg Transaction', money: true, compact: true },
};

/* period_type labels for target rows (I4) — with future-period rows
   filtered, two surviving rows could still share a metric across period
   types; the label makes the goal's cadence explicit. Unknown types render
   the raw backend value rather than nothing. */
const TARGET_PERIOD_LABELS = {
    weekly: ['target_period_weekly', 'Weekly'],
    monthly: ['target_period_monthly', 'Monthly'],
    quarterly: ['target_period_quarterly', 'Quarterly'],
    yearly: ['target_period_yearly', 'Yearly'],
};

/* fillHourlyGaps (Task 6's interior-gap zero-fill for gold.hourly_sales)
   was promoted into the kit when Operational Efficiency became the second
   hourly consumer (W2 Task 9) — imported from './kit' above. */

/* "Empty" = the period produced nothing to report: zero revenue, no trend
   rows and no expenses. Zero revenue with expenses still renders (the cost
   story is decision-grade on its own). */
function isEmptyPayload(d) {
    const kpis = (d && d.kpis) || [];
    const rev = kpis.find((k) => k.name === 'Total Revenue');
    const sections = (d && d.sections) || {};
    const hasTrend = (sections.revenue_trend || []).length > 0;
    const hasExpenses = (sections.expense_categories || []).length > 0;
    return !((rev && Number(rev.value)) || hasTrend || hasExpenses);
}

export default function FinancialPerformanceScreen() {
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const { t, language } = useLanguage();

    // Shared across all Insights surfaces (M1) — survives pops and tab
    // switches; the setter coerces junk ids so rangeForPreset never throws.
    const [preset, setPreset] = useRangePreset();
    const range = useMemo(() => rangeForPreset(preset), [preset]);
    const [status, setStatus] = useState('loading'); // loading|error|empty|ready
    const [data, setData] = useState(null);
    const [narrative, setNarrative] = useState(null);
    const [narrativeStatus, setNarrativeStatus] = useState('loading');
    const [targets, setTargets] = useState(null);

    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    /* Main fetch — stale responses are dropped by sequence, so a quick
       range flick can never paint an older period over a newer one. */
    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        // Returned (W4 Task 13) so PullToRefresh can await it — its spinner
        // stays up until the fetch actually settles, success or failure.
        return reportingService.getFinancialV2(startIso, endIso)
            .then((d) => {
                if (seq !== seqRef.current) return;
                setData(d);
                setStatus(isEmptyPayload(d) ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === seqRef.current) setStatus('error');
            });
    }, [startIso, endIso]);
    useEffect(() => { load(); }, [load]);

    /* Narrative — an enhancement with its OWN status; its failure renders
       nothing and never touches the main surface state. */
    useEffect(() => {
        let stale = false;
        setNarrativeStatus('loading');
        reportingService.getNarrativeV2(startIso, endIso)
            .then((res) => {
                if (stale) return;
                setNarrative(res);
                setNarrativeStatus('ready');
            })
            .catch(() => {
                if (!stale) setNarrativeStatus('error');
            });
        return () => { stale = true; };
    }, [startIso, endIso]);

    /* Targets — read-only enhancement, fetched once; failure just omits
       the section. */
    useEffect(() => {
        let stale = false;
        reportingService.getTargetsV2()
            .then((res) => { if (!stale) setTargets(res); })
            .catch(() => {});
        return () => { stale = true; };
    }, []);

    const kpis = (data && data.kpis) || [];
    const sections = (data && data.sections) || {};

    /* ---- hero: Total Revenue + delta (normaliseKpi owns the quirks;
       rendering is the kit's HeroValue) ---- */
    const revKpi = kpis.find((k) => k.name === 'Total Revenue') || kpis[0] || null;
    const revNorm = revKpi ? normaliseKpi(revKpi) : null;

    /* ---- hero enrichment: Expenses vs Profit ShareBar (W4 Task 13, P6
       recipe) — same two fields the KPI grid tiles below already read
       (Total Expenses, Net Profit), presented as a relationship instead of
       two numbers the reader had to relate by hand. fmtMoneyAuto (M4): both
       figures also render as KPI tiles, which auto-compact at ≥100k — the
       legend must state them the same way. Net Profit can be negative (a
       loss period); ShareBar excludes a negative segment from the bar draw
       but keeps its real value in the legend (Task 3's fix). Omitted
       entirely when both are exactly zero — the collapsed-empty rule: never
       a bar with two £0.00 legend rows and nothing to relate. ---- */
    const expensesKpi = kpis.find((k) => k.name === 'Total Expenses') || null;
    const profitKpi = kpis.find((k) => k.name === 'Net Profit') || null;
    const expensesValue = expensesKpi ? Number(expensesKpi.value) || 0 : 0;
    const profitValue = profitKpi ? Number(profitKpi.value) || 0 : 0;
    const hasShareData = expensesValue !== 0 || profitValue !== 0;
    const shareSegments = [
        {
            id: 'expenses',
            label: t('total_expenses', {}, 'Total Expenses'),
            value: expensesValue,
            display: fmtMoneyAuto(expensesValue, currency),
            tone: 'red',
        },
        {
            id: 'profit',
            label: t('net_profit', {}, 'Net Profit'),
            value: profitValue,
            display: fmtMoneyAuto(profitValue, currency),
            // Sign-aware, matching CostManagementScreen's net_profit segment:
            // a loss period must not render a green dot beside a negative
            // number (spec §9 — colour must never contradict meaning).
            tone: profitValue >= 0 ? 'green' : 'red',
        },
    ];
    const shareAriaLabel = t(
        'expenses_profit_split_aria',
        {
            expenses: fmtMoneyAuto(expensesValue, currency),
            profit: fmtMoneyAuto(profitValue, currency),
        },
        'Expenses {expenses}, Profit {profit}',
    );

    /* ---- KPI grid: every remaining KPI — the count is NOT fixed (the 7th
       "Rev vs Target" appears only when a revenue target spans the period).
       An odd remainder spans its last tile full-width. ---- */
    const restKpis = revKpi ? kpis.filter((k) => k !== revKpi) : kpis;
    const gridItems = restKpis.map((k, i) => {
        const meta = KPI_LABELS[k.name];
        return {
            kpi: k,
            label: meta ? t(meta[0], {}, meta[1]) : k.name,
            format: MONEY_KPIS.has(k.name) ? 'currency' : 'number',
            caption: true,
            ...(restKpis.length % 2 === 1 && i === restKpis.length - 1 ? { span: 2 } : {}),
        };
    });

    /* ---- revenue trend: key off revenue_trend_type; trendLabel handles
       the hour / sales_date / week_start / month_start row shapes. ---- */
    const trend = sections.revenue_trend || [];
    const trendType = sections.revenue_trend_type || 'daily';
    const trendRows = trendType === 'hourly' ? fillHourlyGaps(trend) : trend;
    const trendPoints = trendRows.map((r) => ({
        label: trendLabel(r, trendType, language),
        value: Number(r.revenue) || 0,
    }));

    /* ---- expense breakdown: one ranked list; share of total expenses
       (the web page's pct formula), type carried per row as caption +
       colour instead of two stacked group lists. ---- */
    const rawExpenses = (sections.expense_categories || [])
        .filter((e) => e.category && !String(e.category).toLowerCase().includes('null'));
    const expenseTotal = rawExpenses.reduce((s, e) => s + (Number(e.total_cost) || 0), 0);
    const expenseItems = rawExpenses
        .map((e) => ({
            label: e.category,
            value: Number(e.total_cost) || 0,
            pct: expenseTotal > 0 ? ((Number(e.total_cost) || 0) / expenseTotal) * 100 : 0,
            caption: e.expense_type === 'overhead'
                ? t('overhead', {}, 'Overhead')
                : t('daily', {}, 'Daily'),
            color: e.expense_type === 'overhead' ? 'var(--c-red)' : 'var(--c-amber)',
        }))
        .sort((a, b) => b.value - a.value);

    /* ---- targets (read-only): metric_name → label, target_value → goal,
       actual from the matching KPI; unmatched metrics omitted.
       I4: /targets returns EVERY row with period_end >= today — next
       month's and next quarter's included. Mirror the backend's own
       Rev-vs-Target rule (reporting_v2.py:131): keep only targets whose
       period fully CONTAINS the selected range, and label each surviving
       row with its period_type so two rows can never be confused. ---- */
    const targetRows = ((targets && targets.targets) || [])
        .filter((row) => {
            const ps = String(row.period_start || '').slice(0, 10);
            const pe = String(row.period_end || '').slice(0, 10);
            return ps && pe && ps <= startIso && pe >= endIso;
        })
        .map((row) => {
            const meta = TARGET_METRICS[row.metric_name];
            if (!meta || !(Number(row.target_value) > 0)) return null;
            const kpi = kpis.find((k) => k.name === meta.kpiName);
            if (!kpi || kpi.value == null) return null;
            const periodMeta = TARGET_PERIOD_LABELS[row.period_type];
            const periodLabel = periodMeta
                ? t(periodMeta[0], {}, periodMeta[1])
                : (row.period_type || '');
            const metricLabel = t(meta.labelKey, {}, meta.fallback);
            return {
                key: row.id != null ? row.id : `${row.metric_name}#${row.period_start}`,
                label: periodLabel ? `${metricLabel} · ${periodLabel}` : metricLabel,
                value: Number(kpi.value) || 0,
                target: Number(row.target_value),
                money: meta.money,
                compact: !!meta.compact,
            };
        })
        .filter(Boolean);

    return (
        <div className="fps-wrap">
            <style>{CSS}</style>
            {/* RangeBar sits OUTSIDE SurfaceStates so the range stays
                changeable from the error/empty states. */}
            <RangeBar
                value={preset}
                onChange={(id) => setPreset(id)}
            />
            <PullToRefresh onRefresh={load}>
                <SurfaceStates status={status} onRetry={load}>
                    {/* Hero — Total Revenue + delta vs previous period (kit
                        HeroValue: renders nothing when revNorm is null),
                        with the Expenses vs Profit ShareBar nested inside as
                        the hero's own composition line. */}
                    <HeroValue
                        label={t('total_revenue', {}, 'Total Revenue')}
                        norm={revNorm}
                        currency={currency}
                        testid="fin-hero"
                    >
                        {hasShareData && (
                            <ShareBar segments={shareSegments} ariaLabel={shareAriaLabel} />
                        )}
                    </HeroValue>

                    {/* KPI grid — variable count, quirks absorbed by the kit. */}
                    {gridItems.length > 0 && (
                        <KpiGrid currency={currency} items={gridItems} />
                    )}

                    {/* Revenue trend — ≥4 points draws the chart, fewer
                        renders the kit's stat card. */}
                    <TrendChart
                        title={t('revenue_trend', {}, 'Revenue Trend')}
                        ariaLabel={t('revenue_trend', {}, 'Revenue Trend')}
                        points={trendPoints}
                        formatValue={(v) => fmtMoney(v, currency)}
                    />

                    {/* Expense breakdown — ranked, values always visible. */}
                    <BarList
                        title={t('expense_breakdown', {}, 'Expense Breakdown')}
                        items={expenseItems}
                        formatValue={(v) => fmtMoney(v, currency)}
                    />

                    {/* Targets — READ-ONLY this wave (no edit UI by design). */}
                    {targetRows.length > 0 && (
                        <>
                            <div className="fps-sec">
                                {t('targets_dashboard', {}, 'Targets')}
                            </div>
                            {targetRows.map((row) => (
                                <ProgressVsTarget
                                    key={row.key}
                                    label={row.label}
                                    value={row.value}
                                    target={row.target}
                                    formatValue={row.money
                                        // compact → the tile pairing (M4);
                                        // full → the hero pairing (revenue).
                                        ? (v) => (row.compact
                                            ? fmtMoneyAuto(v, currency)
                                            : fmtMoney(v, currency))
                                        : (v) => `${Number(v).toFixed(1)}%`}
                                />
                            ))}
                        </>
                    )}

                    {/* AI narrative — separate status; failure renders
                        nothing. */}
                    <NarrativeCard
                        state={narrativeStatus}
                        text={narrative && narrative.narrative}
                        generatedAt={narrative && narrative.generated_at}
                    />

                    {/* Insights — the endpoint's insights[] plus
                        /narrative's anomalies[] (M7: adapted through
                        mapAnomaly — raw anomalies have no title/description
                        and would render as blank tinted rows). */}
                    <InsightList
                        items={[
                            ...((data && data.insights) || []),
                            ...(((narrative && narrative.anomalies) || [])
                                .map(mapAnomaly)
                                .filter(Boolean)),
                        ]}
                    />
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
